import {
    closestCenter,
    DndContext,
    KeyboardSensor,
    MouseSensor,
    useSensor,
    useSensors
} from "@dnd-kit/core";
import {
    arrayMove,
    horizontalListSortingStrategy,
    SortableContext,
    sortableKeyboardCoordinates,
    verticalListSortingStrategy
} from "@dnd-kit/sortable";
import {useCallback, useEffect, useState} from "react";
import type {ComponentProps} from "react";

import type {SortableListProps} from "./type";

type DndContextProps = ComponentProps<typeof DndContext>;

function SortableList<T extends {id: string | number}>({
    initItems,
    strategy,
    onDragEnd,
    renderItem
}: SortableListProps<T>) {
    const [items, setItems] = useState(initItems);

    // there is a case that removes the item from the list
    useEffect(() => {
        setItems(initItems);
    }, [initItems]);

    const sensors = useSensors(
        useSensor(MouseSensor, {
            // Require the mouse to move by 10 pixels before activating
            activationConstraint: {
                distance: 10
            }
        }),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates
        })
    );

    const handleDragEnd = useCallback<NonNullable<DndContextProps["onDragEnd"]>>(
        event => {
            const {active, over} = event;
            if (over?.id && active.id !== over.id) {
                setItems(prev => {
                    const items = prev.map(item => item.id);
                    const oldIndex = items.indexOf(active.id as string);
                    const newIndex = items.indexOf(over.id as string);
                    const next = arrayMove(prev, oldIndex, newIndex);
                    onDragEnd(next);
                    return next;
                });
            }
        },
        [onDragEnd]
    );

    return (
        <DndContext collisionDetection={closestCenter} sensors={sensors} onDragEnd={handleDragEnd}>
            <SortableContext
                items={items}
                strategy={
                    strategy === "vertical"
                        ? verticalListSortingStrategy
                        : horizontalListSortingStrategy
                }
            >
                {items.map(item => renderItem(item))}
            </SortableContext>
        </DndContext>
    );
}

export default SortableList;
